import { useTranslation } from "react-i18next"

import type { HostedAgentToolCatalogEntry } from "../hooks/admin-queries"
import type { HostedAgentFormState } from "../utils/hosted-agents"

import { Checkbox } from "@/components/ui/checkbox"
import { cn } from "@/lib/utils"

export function HostedAgentActionPicker({
  tool,
  form,
  toggleAction,
}: {
  tool: HostedAgentToolCatalogEntry
  form: HostedAgentFormState
  toggleAction: (params: {
    tool: string
    action: string
    allActions: string[]
    checked: boolean
  }) => void
}) {
  const { t } = useTranslation()

  if (tool.actions.length === 0) return null
  const allowlist = form.toolActions[tool.name] ?? []
  const restricted = allowlist.length > 0
  return (
    <div className="bg-muted/30 mt-2 space-y-2 rounded-md border p-3">
      <p className="text-xs font-medium">
        {t("adminDashboard.hostedAgents.toolsTab.actionsTitle", {
          defaultValue: "Allowed actions",
        })}
      </p>
      <p className="text-muted-foreground text-xs">
        {t("adminDashboard.hostedAgents.toolsTab.actionsHint", {
          defaultValue:
            "All actions are allowed by default. Select a subset to restrict this agent to only those actions.",
        })}
      </p>
      {tool.approvalGatedActions.length > 0 ? (
        <p className="border-foreground bg-warning text-warning-foreground rounded-md border px-2 py-1 text-xs">
          {t("adminDashboard.hostedAgents.toolsTab.actionsApprovalGatedHint", {
            defaultValue:
              "These actions need user approval and are never offered to a hosted agent: {{actions}}",
            actions: tool.approvalGatedActions.join(", "),
          })}
        </p>
      ) : null}
      <div className="grid gap-1.5 sm:grid-cols-2">
        {tool.actions.map((action) => {
          const allowed = !restricted || allowlist.includes(action)
          const gated = tool.approvalGatedActions.includes(action)
          return (
            <label
              key={action}
              className="hover:bg-accent/50 flex cursor-pointer items-center gap-2 rounded-sm p-1 text-xs"
            >
              <Checkbox
                checked={allowed}
                onCheckedChange={(value) =>
                  toggleAction({
                    tool: tool.name,
                    action,
                    allActions: tool.actions,
                    checked: value === true,
                  })
                }
              />
              <span className={cn("font-mono", gated && "text-muted-foreground line-through")}>
                {action}
              </span>
            </label>
          )
        })}
      </div>
    </div>
  )
}
